import { BookOpen, Gift, RotateCcw } from "lucide-react";
import { AvatarDisplay } from "./AvatarDisplay";

const CARD_GRADIENTS = [
  { from: "#118AB2", to: "#06D6A0" },
  { from: "#EF476F", to: "#FFD166" },
  { from: "#9B5DE5", to: "#4CC9F0" },
  { from: "#F77F00", to: "#FFD166" },
  { from: "#06D6A0", to: "#118AB2" },
  { from: "#EF476F", to: "#9B5DE5" },
];

export default function ChildCard({ child, onSelect, onEdit, onDelete, onSetPrize, onResetLesson, t }) {
  const ageLabels = {
    little: t('little_light'),
    big: t('bible_explorer'),
    teen: t('faith_builder')
  };
  const ageLabel = ageLabels[child.age_group] || t('bible_explorer');
  const idx = child.id?.charCodeAt(0) % CARD_GRADIENTS.length || 0;
  const grad = CARD_GRADIENTS[idx];

  return (
    <div className="bg-white rounded-3xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:scale-[1.02] cursor-pointer border-b-4"
      style={{ borderColor: grad.from }}
      data-testid={`child-card-${child.id}`}
      onClick={onSelect}>
      {/* Colorful top strip */}
      <div className="h-3 w-full" style={{ background: `linear-gradient(90deg, ${grad.from}, ${grad.to})` }} />

      <div className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <AvatarDisplay avatar={child.avatar} size={52} />
            <div>
              <h3 className="font-black text-lg text-[#073B4C]" style={{ fontFamily: "Fredoka, sans-serif" }}>{child.name}</h3>
              <span className="text-xs font-bold px-2 py-0.5 rounded-full text-white"
                style={{ background: grad.from }}>
                {ageLabel} · {t('age')} {child.age}
              </span>
            </div>
          </div>
          {child.today_completed && (
            <div className="rounded-full p-1.5" style={{ background: "#E0FFF5" }}>
              <BookOpen size={16} color="#06D6A0" strokeWidth={2.5} />
            </div>
          )}
        </div>

        <div className="grid grid-cols-3 gap-2 mb-4">
          {[
            { label: t('lessons'), value: child.total_lessons || 0, color: "#118AB2", bg: "#E0F4FF" },
            { label: t('stars'),   value: child.total_stars   || 0, color: "#F77F00", bg: "#FFF3CD" },
            { label: t('streak'),  value: `${child.streak||0}d`,  color: "#9B5DE5", bg: "#F0E6FF" },
          ].map((s, i) => (
            <div key={i} className="text-center rounded-xl py-2" style={{ background: s.bg }}>
              <p className="text-base font-black" style={{ color: s.color }}>{s.value}</p>
              <p className="text-xs font-semibold" style={{ color: s.color, opacity: 0.7 }}>{s.label}</p>
            </div>
          ))}
        </div>

        <div className="flex gap-2 flex-wrap">
          <button onClick={e => { e.stopPropagation(); onSelect(); }}
            className="flex-1 text-white font-bold py-2 rounded-xl text-sm transition-all hover:opacity-90"
            style={{ background: `linear-gradient(90deg, ${grad.from}, ${grad.to})` }}
            data-testid={`open-child-${child.id}`}>{t('open')}</button>
          <button onClick={e => { e.stopPropagation(); onSetPrize(); }}
            className="px-3 bg-amber-50 hover:bg-amber-100 text-amber-600 font-bold py-2 rounded-xl text-sm flex items-center gap-1"
            data-testid={`set-prize-${child.id}`}>
            <Gift size={14} /> {t('prize')}
          </button>
          <button onClick={e => { e.stopPropagation(); onEdit(); }}
            className="px-3 bg-slate-100 hover:bg-slate-200 text-slate-600 font-bold py-2 rounded-xl text-sm"
            data-testid={`edit-child-${child.id}`}>{t('edit')}</button>
          <button onClick={e => { e.stopPropagation(); onDelete(); }}
            className="px-3 bg-red-50 hover:bg-red-100 text-red-400 font-bold py-2 rounded-xl text-sm"
            data-testid={`delete-child-${child.id}`}>{t('del')}</button>
        </div>
        
        {/* Reset Lesson Button - only shows when lesson is completed */}
        {child.today_completed && onResetLesson && (
          <button 
            onClick={e => { e.stopPropagation(); onResetLesson(); }}
            className="mt-2 w-full bg-orange-50 hover:bg-orange-100 text-orange-600 font-bold py-2 rounded-xl text-sm flex items-center justify-center gap-2 transition-all"
            data-testid={`reset-lesson-${child.id}`}>
            <RotateCcw size={14} /> {t('reset_today_lesson') || "Reset Today's Lesson"}
          </button>
        )}
      </div>
    </div>
  );
}
